import { IUserAccess, IUserAccessInput } from '@zeta-core';

type UserAccessFeatures = IUserAccess['access'] | IUserAccessInput['access'] | undefined;
type AccessFeatureConfig = {
  Type?: 'Read' | 'Write';
  type?: 'Read' | 'Write';
  StatusCondition?: string;
  status_condition?: string;
  DisabledSubFeatures?: string[];
  disabled_sub_features?: string[];
};

const getPermissionType = (config: AccessFeatureConfig) => config.Type ?? config.type ?? '';

const getStatusCondition = (config: AccessFeatureConfig) => config.StatusCondition ?? config.status_condition ?? '';

const SUB_FEATURE_NAME_MAP: Record<string, string> = {
  usermanagement: 'usermanagement',
  billingmanagement: 'billingmanagement',
  managedportfolio: 'managedportfolio',
  products: 'products',
  clientcommunication: 'clientcommunication',
};

const normalizeSubFeatureName = (subFeatureName: string) => SUB_FEATURE_NAME_MAP[subFeatureName.replace(/[^a-z]/gi, '').toLowerCase()] ?? subFeatureName;

export const getDisabledSubFeatures = (features: UserAccessFeatures, featureName: string): string[] => {
  const featureConfig = (features && features[featureName]) as AccessFeatureConfig[] | undefined;

  if (!featureConfig?.length) {
    return [];
  }

  return Array.from(
    new Set(
      featureConfig.flatMap((config) => {
        const disabledSubFeatures = config.DisabledSubFeatures ?? config.disabled_sub_features ?? [];
        return disabledSubFeatures.map(normalizeSubFeatureName);
      })
    )
  );
};

export const hasSubFeatureAccess = (features: UserAccessFeatures, featureName: string, subFeatureName: string): boolean => {
  const { accessible } = getFeaturePermission(features, featureName);

  if (!accessible) {
    return false;
  }

  return !getDisabledSubFeatures(features, featureName).includes(normalizeSubFeatureName(subFeatureName));
};

/**
 * Gets detailed permission information for a specific feature
 * @param features The features object from userAccess store
 * @param featureName The name of the feature to check
 * @returns Object containing accessibility info and permission types
 */
export const getFeaturePermission = (
  features: UserAccessFeatures,
  featureName: string
): {
  accessible: boolean;
  hasReadPermission: boolean;
  hasWritePermission: boolean;
  permissions: { type: string; statusCondition: string }[];
  featureConfig: { Type: string; StatusCondition: string; DisabledSubFeatures: string[] }[] | null;
} => {
  const rawFeatureConfig = (features && features[featureName]) as AccessFeatureConfig[] | undefined;

  // Default return when feature is not found
  if (!rawFeatureConfig) {
    return {
      accessible: false,
      hasReadPermission: false,
      hasWritePermission: false,
      permissions: [],
      featureConfig: null,
    };
  }

  const featureConfig = rawFeatureConfig.map((config) => ({
    Type: getPermissionType(config),
    StatusCondition: getStatusCondition(config),
    DisabledSubFeatures: (config.DisabledSubFeatures ?? config.disabled_sub_features ?? []).map(normalizeSubFeatureName),
  }));

  // Extract all permission types and status conditions
  const permissions = featureConfig.map((config) => ({
    type: config.Type,
    statusCondition: config.StatusCondition,
  }));

  // Check for specific permission types
  const hasReadPermission = featureConfig.some((config) => config.Type === 'Read');
  const hasWritePermission = featureConfig.some((config) => config.Type === 'Write');

  return {
    accessible: true,
    hasReadPermission,
    hasWritePermission,
    permissions,
    featureConfig,
  };
};

/**
 * Checks access type for features based on StatusCondition
 * @param features The features object from userAccess store
 * @param featureKey The key of the feature to check (e.g., 'Advisor-Client-FF')
 * @param statusCondition The specific status condition to check for (e.g., 'advise', 'recommendation')
 * @returns Object containing hasAccess (boolean) and isReadOnly (boolean) properties
 */
export const getClientFeatureAccess = (
  features: UserAccessFeatures,
  featureKey: string,
  statusCondition: string
): { hasAccess: boolean; isReadOnly: boolean } => {
  const featureConfig = (features && features[featureKey]) as AccessFeatureConfig[] | undefined;
  // Default return value - no access
  const defaultResult = { hasAccess: false, isReadOnly: true };

  // If feature doesn't exist, return no access
  if (!featureConfig) {
    return defaultResult;
  }

  // Find a matching configuration with the provided StatusCondition
  const matchingConfig = featureConfig.find((config) => getStatusCondition(config) === statusCondition);

  // If no matching config found, return no access
  if (!matchingConfig) {
    return defaultResult;
  }

  // If matching config found, determine access type
  return {
    hasAccess: true,
    isReadOnly: getPermissionType(matchingConfig) === 'Read',
  };
};
